import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Body2,
} from "@fluentui/react-components";
import { InfoRegular } from "@fluentui/react-icons";
import moment from "moment";
import { getPayoutBreakdown } from "../../services/expectedHoursService";
import type { IWorktimePayoutBreakdownLine } from "../../services/types/expectedHours";
import dataTimeService from "../../services/dataTime.service";
import appErrorToast from "../common/AppErrorToast";
import { payoutBreakdownStyles } from "./PayoutBreakdownInfoButtonStyles";

function documentTypeLabel(documentType: string): string {
  return documentType === "Commision" ? "Zlecenie" : "Wniosek";
}

function applicationTypeLabel(applicationType: string | null): string {
  if (!applicationType) return "—";
  if (applicationType === "Overtime") return "Nadgodziny";
  if (applicationType === "Leaving") return "Wyjście";
  return applicationType;
}

type PayoutBucket = 100 | 150 | 200;

const cache = new Map<string, IWorktimePayoutBreakdownLine[]>();

function cacheKey(userId: string, year: number, month: number, bucket: PayoutBucket): string {
  return `${userId}|${year}|${month}|${bucket}`;
}

interface PayoutBreakdownInfoButtonProps {
  userId: string;
  year: number;
  month: number;
  bucket: PayoutBucket;
  ariaLabel: string;
}

const PayoutBreakdownInfoButton: React.FC<PayoutBreakdownInfoButtonProps> = ({
  userId,
  year,
  month,
  bucket,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<IWorktimePayoutBreakdownLine[] | null>(null);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const key = cacheKey(userId, year, month, bucket);
    const cached = cache.get(key);
    if (cached) {
      setLines(cached);
      setLoading(false);
      return;
    }
    try {
      const data = await getPayoutBreakdown(userId, year, month, bucket);
      cache.set(key, data);
      if (mounted.current) setLines(data);
    } catch {
      appErrorToast("Nie udało się pobrać szczegółów rozliczenia.");
      if (mounted.current) setLines([]);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [userId, year, month, bucket]);

  useEffect(() => {
    if (!open) return;
    setLines(null);
    setLoading(true);
    void load();
  }, [open, load]);

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open || false)}>
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          size="small"
          icon={<InfoRegular />}
          aria-label={ariaLabel}
        />
      </PopoverTrigger>
      <PopoverSurface tabIndex={-1} style={payoutBreakdownStyles.popoverSurface}>
        <Body2 style={payoutBreakdownStyles.title}>
          Realizacje ({bucket}%)
        </Body2>
        {loading && (
          <div style={payoutBreakdownStyles.spinnerRow}>
            <Spinner size="small" />
          </div>
        )}
        {!loading && lines && lines.length === 0 && (
          <Body2>Brak pozycji do wyświetlenia.</Body2>
        )}
        {!loading && lines && lines.length > 0 && (
          <table style={payoutBreakdownStyles.table}>
            <thead>
              <tr>
                <th style={payoutBreakdownStyles.th}>Data realizacji</th>
                <th style={payoutBreakdownStyles.th}>Czas (real.)</th>
                <th style={payoutBreakdownStyles.th}>Projekt</th>
                <th style={payoutBreakdownStyles.th}>Typ</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.assignmentId}>
                  <td style={payoutBreakdownStyles.td}>
                    {moment(line.realizationDate).format("YYYY-MM-DD")}
                  </td>
                  <td style={payoutBreakdownStyles.td}>
                    {dataTimeService.convertToTimeFormat(line.durationMinutes)}
                  </td>
                  <td style={payoutBreakdownStyles.td}>
                    {line.youTrackProjectLabel ?? "—"}
                  </td>
                  <td style={payoutBreakdownStyles.td}>
                    {documentTypeLabel(line.documentType)}
                    {line.applicationType ? ` / ${applicationTypeLabel(line.applicationType)}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PopoverSurface>
    </Popover>
  );
};

export default PayoutBreakdownInfoButton;
